import { useQuery } from "@tanstack/react-query";
import orderCore from "../../core/order.core";
import { Cart } from "../../types/cart";
import { useRouter } from "next/router";
import { useTranslation } from "../../core/translation.core";
import { useCookies } from "../../core/cookie.core";
import { Order } from "../../types/order";
import { formatDate } from "../../core/date.core";
import { useRef } from "react";
import CombineOrderDetail from "./combineOrderDetail.comp";
import XMarkIcon from "../../icons/xMark.icon";
import { LoadingComp } from "../common/loading.comp";
import { useCart } from "../../hooks/cart/useCart.hook";
import useCheckoutSession from "../common/useCheckoutSession.hook";

export default function CombineOrder() {
  const router = useRouter();
  const { t, msg } = useTranslation();

  const tempSessionId = useCookies("tempSessionId");
  const { data: checkoutSession } = useCheckoutSession(true);
  const shipOptionId = checkoutSession?.shipOptionId;

  const { cart } = useCart();

  const hasGiftCardItems = cart?.items.some((item) => item.product.productType === "giftCard") ?? false;

  const { data, isLoading } = useQuery({
    queryKey: ["Combine-Order", cart, shipOptionId],
    queryFn: () => orderCore.getCombineOrder({ sessionId: tempSessionId!, shipOptionId: shipOptionId! }),
    enabled: !!tempSessionId && !!shipOptionId && cart && cart.items.length > 0 && !hasGiftCardItems,
    retry: 1,
  });

  function handleCombineOrder(orderId: string) {
    router.push(`/checkout/combineOrder?orderId=${orderId}`);
  }

  if (hasGiftCardItems) return null;

  if (isLoading) {
    return (
      <div className="py-5">
        <LoadingComp sizeClassName="h-10 w-10" />
      </div>
    );
  }

  if (!data || data.orders.length < 1 || !shipOptionId) return null;

  return (
    <div className="w-full my-4">
      <h2 className="text-lg font-bold mb-2">合併訂單</h2>
      <p className="text-xs mb-2">{t(msg.order.combineOrderMessage)}</p>
      {data.orders.map((order) => (
        <CombineOrderItem key={order.id} order={order} onCombine={handleCombineOrder} />
      ))}
    </div>
  );
}

interface CombineOrderItemProps {
  order: Order;
  onCombine: (orderId: string) => void;
}

function CombineOrderItem({ order, onCombine }: CombineOrderItemProps) {
  const { t, msg } = useTranslation();

  const modalRef = useRef<HTMLDialogElement>(null);

  function showModal() {
    modalRef.current?.showModal();
  }

  return (
    <div className="flex justify-between mb-1">
      <p>#{order.orderNumber}</p>
      <p>{formatDate(order.createdAt!)}</p>
      <button
        type="button"
        onClick={showModal}
        className="flex-center bg-primary-light text-white py-1 px-2 w-fit rounded-lg disabled:bg-gray-500 disabled:cursor-not-allowed"
      >
        {t(msg.order.view)}
      </button>
      <dialog ref={modalRef} className="rounded-lg max-w-[100vw] max-h-[100vh] sm:w-[600px] sm:h-[600px]">
        <button type="button" className="absolute top-4 right-4" onClick={() => modalRef.current?.close()}>
          <XMarkIcon />
        </button>
        <div className="bg-white">
          <CombineOrderDetail order={order} onCombine={onCombine} />
        </div>
      </dialog>
    </div>
  );
}
